function toUint8Array(chunk) {
  if (chunk instanceof Uint8Array) return chunk
  if (chunk instanceof ArrayBuffer) return new Uint8Array(chunk)
  if (ArrayBuffer.isView(chunk)) {
    return new Uint8Array(chunk.buffer, chunk.byteOffset, chunk.byteLength)
  }
  return new Uint8Array(0)
}

export function createNdjsonParser(onEvent) {
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  const drain = () => {
    let newline = buffer.indexOf('\n')
    while (newline >= 0) {
      const line = buffer.slice(0, newline).trim()
      buffer = buffer.slice(newline + 1)
      if (line) onEvent(JSON.parse(line))
      newline = buffer.indexOf('\n')
    }
  }

  return {
    push(chunk) {
      buffer += typeof chunk === 'string'
        ? chunk
        : decoder.decode(toUint8Array(chunk), { stream: true })
      drain()
    },
    end() {
      buffer += decoder.decode()
      drain()
      const tail = buffer.trim()
      buffer = ''
      if (tail) onEvent(JSON.parse(tail))
    },
  }
}

function cancelEndpoint(endpoint) {
  return endpoint.replace(/\/stream\/?$/, '/cancel')
}

export function createMastraChatStream({
  endpoint,
  chatTicket,
  fetchImpl = typeof fetch === 'function' ? fetch : undefined,
  onEvent = () => {},
  onError = () => {},
  onDone = () => {},
}) {
  if (!endpoint || !chatTicket) throw new Error('mastra_route_invalid')
  if (typeof fetchImpl !== 'function') throw new Error('mastra_fetch_unavailable')

  let abortController = null
  let executionId = ''
  let terminalType = ''

  async function start(payload) {
    abortController = new AbortController()
    executionId = ''
    terminalType = ''
    const parser = createNdjsonParser(event => {
      if (event?.executionId) executionId = String(event.executionId)
      if (['run_completed', 'run_failed', 'run_cancelled'].includes(event?.type)) {
        terminalType = event.type
      }
      onEvent(event)
    })

    try {
      const response = await fetchImpl(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Chat-Ticket': chatTicket,
        },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      })
      if (!response.ok) throw new Error(`mastra_http_${response.status}`)

      if (response.body?.getReader) {
        const reader = response.body.getReader()
        let reading = true
        while (reading) {
          const { done, value } = await reader.read()
          if (done) reading = false
          else parser.push(value)
        }
      } else {
        parser.push(await response.text())
      }
      parser.end()

      if (!terminalType) throw new Error('mastra_stream_incomplete')
      const result = { executionId, terminalType }
      onDone(result)
      return result
    } catch (error) {
      const normalized = error instanceof Error ? error : new Error('mastra_stream_failed')
      onError(normalized)
      throw normalized
    }
  }

  async function cancel() {
    if (!executionId) {
      abortController?.abort()
      return false
    }

    try {
      const response = await fetchImpl(cancelEndpoint(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Chat-Ticket': chatTicket,
        },
        body: JSON.stringify({ executionId }),
      })
      return response.ok
    } finally {
      abortController?.abort()
    }
  }

  return {
    start,
    cancel,
    close() {
      abortController?.abort()
    },
    executionId: () => executionId,
  }
}
